import React, { useState, useEffect, useCallback } from 'react';
import { PromptService } from '../services/promptService';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PromptTemplate from './PromptTemplate';
import PromptPreview from './PromptPreview';
import SavedPrompts from './SavedPrompts';
import PromptCategories from './PromptCategories';
import Settings from './Settings';
import Footer from './Footer';
import { useAuth } from './AuthProvider';
import { useChatbot } from './ChatbotProvider';
import Instructions from './Instructions';

const { FiCode, FiSave, FiCopy, FiRefreshCw, FiBookOpen, FiZap, FiSettings, FiUser, FiLogOut, FiCheck, FiAlertCircle, FiMenu, FiX, FiEdit3, FiLayers, FiGlobe, FiLock, FiShield, FiDatabase, FiCpu, FiSmartphone, FiSearch, FiMail, FiPhone, FiMapPin, FiClock, FiImage, FiVideo, FiFile, FiUsers, FiBarChart2, FiShoppingCart, FiCalendar, FiBook, FiDollarSign, FiHeart, FiCamera, FiMusic, FiGamepad2, FiTool, FiCloud, FiInfo, FiExternalLink, FiLink, FiCopy: FiCopyIcon } = FiIcons;

// Helper component for field descriptions
const FieldDescription = ({ children }) => (
  <p className="mt-1 text-xs text-slate-500 flex items-start">
    <SafeIcon icon={FiInfo} className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
    <span>{children}</span>
  </p>
);

const initialPromptData = {
  // Site Identity
  siteTitle: 'My Awesome App',
  logoUrl: '',
  faviconUrl: '',
  // Project Configuration
  category: 'web-development',
  projectType: 'react-app',
  customCategory: '',
  customProjectType: '',
  complexity: 'intermediate',
  framework: 'react',
  styling: 'tailwind',
  features: ['responsive', 'animations'],
  customRequirements: '',
  buildNotes: '',
  // Additional Context
  context: '',
  constraints: '',
  // PWA & SEO
  isPwa: false,
  hideFromSearchEngines: false,
  // Design Reference
  designConceptsUrl: '',
  includeSitemapAnalysis: false,
  includeContentStructure: false,
  // Admin Console
  includeAdminConsole: false,
  adminFeatures: {
    contentManagement: true,
    userManagement: true,
    analytics: true,
    mediaStorage: true,
    customBranding: true,
    seoManagement: true,
    businessInfo: false,
    operatingHours: false,
    onlineBooking: false,
    menuManagement: false,
    rolePermissions: false,
    backupRestore: false,
    apiManagement: false,
    customFields: false
  },
  adminCredentials: {
    useCustomCredentials: false,
    username: '',
    password: ''
  },
  // Business Information
  businessInfo: {
    companyName: '',
    businessEmail: '',
    phoneNumber: '',
    websiteUrl: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    country: '',
    socialFacebook: '',
    socialTwitter: '',
    socialInstagram: '',
    socialLinkedin: '',
    socialYoutube: ''
  },
  // Additional Pages
  includePrivacyPage: false,
  includeTermsPage: false,
  includeContactPage: false,
  contactFormEnabled: true,
  // Output Format
  outputFormat: 'complete-project',
  // Integrations
  backendDatabase: 'supabase',
  hostServer: 'cloudflare-pages',
  storageOption: 'dropbox',
  // AI Integration
  aiProvider: 'openai',
  aiModel: 'gpt-4o-mini'
};

const categoryOptions = [
  { value: 'web-development', label: 'Web Development', icon: FiCode },
  { value: 'mobile-app', label: 'Mobile Apps', icon: FiSmartphone },
  { value: 'data-analysis', label: 'Data Analysis', icon: FiBarChart2 },
  { value: 'automation', label: 'Automation Tools', icon: FiZap },
  { value: 'ui-design', label: 'UI/UX Design', icon: FiImage },
  { value: 'api-integration', label: 'API Integration', icon: FiDatabase },
  { value: 'e-commerce', label: 'E-commerce', icon: FiShoppingCart },
  { value: 'content-management', label: 'Content Management', icon: FiBook },
  { value: 'entertainment', label: 'Entertainment', icon: FiMusic },
  { value: 'productivity', label: 'Productivity', icon: FiTool },
  { value: 'health-fitness', label: 'Health & Fitness', icon: FiHeart },
  { value: 'education', label: 'Education', icon: FiBookOpen },
  { value: 'finance', label: 'Finance', icon: FiDollarSign },
  { value: 'real-estate', label: 'Real Estate', icon: FiMapPin },
  { value: 'communication', label: 'Communication', icon: FiUsers },
  { value: 'calendar-events', label: 'Calendar & Events', icon: FiCalendar },
  { value: 'travel', label: 'Travel & Tourism', icon: FiGlobe },
  { value: 'gaming', label: 'Gaming', icon: FiGamepad2 },
  { value: 'media-photography', label: 'Media & Photography', icon: FiCamera },
  { value: 'security', label: 'Security & Privacy', icon: FiLock },
  { value: 'cloud-storage', label: 'Cloud & Storage', icon: FiCloud },
  { value: 'monitoring', label: 'Monitoring & Analytics', icon: FiBarChart2 },
  { value: 'other', label: 'Other', icon: FiEdit3 }
];

const projectTypeOptions = {
  'web-development': [
    { value: 'react-app', label: 'React Application' },
    { value: 'landing-page', label: 'Landing Page' },
    { value: 'dashboard', label: 'Dashboard' },
    { value: 'e-commerce', label: 'E-commerce Store' },
    { value: 'portfolio', label: 'Portfolio Website' },
    { value: 'blog', label: 'Blog Platform' },
    { value: 'cms', label: 'Content Management System' },
    { value: 'social-media', label: 'Social Media App' },
    { value: 'marketplace', label: 'Marketplace' },
    { value: 'other', label: 'Other' }
  ],
  'mobile-app': [
    { value: 'react-app', label: 'React Mobile App' },
    { value: 'pwa', label: 'Progressive Web App' },
    { value: 'hybrid', label: 'Hybrid Mobile App' },
    { value: 'native', label: 'Native Mobile App' },
    { value: 'other', label: 'Other' }
  ],
  'data-analysis': [
    { value: 'dashboard', label: 'Analytics Dashboard' },
    { value: 'reporting', label: 'Reporting Tool' },
    { value: 'visualization', label: 'Data Visualization' },
    { value: 'bi-tool', label: 'Business Intelligence Tool' },
    { value: 'other', label: 'Other' }
  ],
  'automation': [
    { value: 'workflow', label: 'Workflow Automation' },
    { value: 'task-runner', label: 'Task Runner' },
    { value: 'scheduler', label: 'Scheduling Tool' },
    { value: 'integration', label: 'Integration Platform' },
    { value: 'other', label: 'Other' }
  ],
  'other': [
    { value: 'other', label: 'Custom Project' }
  ]
};

const complexityOptions = [
  { value: 'beginner', label: 'Beginner - Simple functionality' },
  { value: 'intermediate', label: 'Intermediate - Standard features' },
  { value: 'advanced', label: 'Advanced - Complex functionality' }
];

const frameworkOptions = [
  { value: 'react', label: 'React' },
  { value: 'vue', label: 'Vue.js' },
  { value: 'angular', label: 'Angular' },
  { value: 'svelte', label: 'Svelte' },
  { value: 'nextjs', label: 'Next.js' },
  { value: 'nuxtjs', label: 'Nuxt.js' },
  { value: 'vanilla-js', label: 'Vanilla JavaScript' },
  { value: 'node-js', label: 'Node.js' },
  { value: 'python', label: 'Python (Flask/Django)' },
  { value: 'html-css', label: 'HTML/CSS Only' }
];

const stylingOptions = [
  { value: 'tailwind', label: 'Tailwind CSS' },
  { value: 'css-modules', label: 'CSS Modules' },
  { value: 'styled-components', label: 'Styled Components' },
  { value: 'vanilla-css', label: 'Vanilla CSS' },
  { value: 'sass', label: 'Sass/SCSS' },
  { value: 'bootstrap', label: 'Bootstrap' },
  { value: 'material-ui', label: 'Material UI' }
];

const featureOptions = [
  { id: 'responsive', label: 'Responsive Design', category: 'core' },
  { id: 'animations', label: 'Animations & Transitions', category: 'ui' },
  { id: 'dark-mode', label: 'Dark Mode Toggle', category: 'ui' },
  { id: 'auth', label: 'User Authentication', category: 'backend' },
  { id: 'database', label: 'Database Integration', category: 'backend' },
  { id: 'api', label: 'API Integration', category: 'backend' },
  { id: 'search', label: 'Search Functionality', category: 'core' },
  { id: 'filters', label: 'Advanced Filtering', category: 'core' },
  { id: 'real-time', label: 'Real-time Updates', category: 'backend' },
  { id: 'notifications', label: 'Push Notifications', category: 'pwa' },
  { id: 'offline-support', label: 'Offline Functionality', category: 'pwa' },
  { id: 'service-worker', label: 'Service Worker', category: 'pwa' },
  { id: 'app-shell', label: 'App Shell Architecture', category: 'pwa' },
  { id: 'installable', label: 'Installable Experience', category: 'pwa' },
  { id: 'background-sync', label: 'Background Sync', category: 'pwa' },
  { id: 'touch-gestures', label: 'Touch Gestures', category: 'mobile' },
  { id: 'camera', label: 'Camera Integration', category: 'device' },
  { id: 'geolocation', label: 'Geolocation Services', category: 'device' },
  { id: 'file-upload', label: 'File Upload', category: 'core' },
  { id: 'drag-drop', label: 'Drag & Drop', category: 'ui' },
  { id: 'charts', label: 'Data Visualization', category: 'data' },
  { id: 'export', label: 'Data Export', category: 'data' },
  { id: 'import', label: 'Data Import', category: 'data' },
  { id: 'social-sharing', label: 'Social Sharing', category: 'social' },
  { id: 'payment', label: 'Payment Integration', category: 'ecommerce' },
  { id: 'cart', label: 'Shopping Cart', category: 'ecommerce' },
  { id: 'wishlist', label: 'Wishlist', category: 'ecommerce' },
  { id: 'reviews', label: 'User Reviews', category: 'social' },
  { id: 'ratings', label: 'Rating System', category: 'social' },
  { id: 'comments', label: 'Commenting System', category: 'social' }
];

const outputFormatOptions = [
  { value: 'complete-project', label: 'Complete Project Structure' },
  { value: 'single-component', label: 'Single Component' },
  { value: 'function', label: 'Function/Utility' },
  { value: 'api-endpoint', label: 'API Endpoint' },
  { value: 'database-schema', label: 'Database Schema' }
];

// Integration options
const backendDatabaseOptions = [
  { value: 'supabase', label: 'Supabase' },
  { value: 'no-code-backend', label: 'No Code Backend' }
];

const hostServerOptions = [
  { value: 'cloudflare-pages', label: 'Cloudflare Pages' },
  { value: 'netlify', label: 'Netlify' }
];

const storageOptionOptions = [
  { value: 'dropbox', label: 'Dropbox' },
  { value: 'netlify-blobs', label: 'Netlify Blobs' },
  { value: 'google-drive', label: 'Google Drive' },
  { value: 'aws', label: 'Amazon AWS' }
];

// AI Integration options
const aiProviderOptions = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'google', label: 'Google Gemini' },
  { value: 'mistral', label: 'Mistral AI' }
];

const aiModelOptions = {
  'openai': [
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Default)' },
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { value: 'gpt-4', label: 'GPT-4' },
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' }
  ],
  'anthropic': [
    { value: 'claude-3-5-sonnet', label: 'Claude 3.5 Sonnet' },
    { value: 'claude-3-opus', label: 'Claude 3 Opus' },
    { value: 'claude-3-sonnet', label: 'Claude 3 Sonnet' },
    { value: 'claude-3-haiku', label: 'Claude 3 Haiku' }
  ],
  'google': [
    { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
    { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' },
    { value: 'gemini-1.0-pro', label: 'Gemini 1.0 Pro' }
  ],
  'mistral': [
    { value: 'mistral-large', label: 'Mistral Large' },
    { value: 'mistral-medium', label: 'Mistral Medium' },
    { value: 'mistral-small', label: 'Mistral Small' }
  ]
};

function getUserIdMaybe() {
  try {
    // If you have an auth provider,adapt this hook usage accordingly later:
    const fromStorage = localStorage.getItem('user_id');
    return fromStorage || 'anonymous';
  } catch {
    return 'anonymous';
  }
}

async function migrateLocalPromptsOnce() {
  const FLAG = 'migrated_local_prompts_to_ncb';
  if (localStorage.getItem(FLAG)) return;

  const raw = localStorage.getItem('savedPrompts');
  if (!raw) return;

  try {
    const arr = JSON.parse(raw);
    const userId = getUserIdMaybe();
    if (Array.isArray(arr) && arr.length) {
      for (const p of arr) {
        try {
          const data = p.data || {};
          const text = p.prompt || '';
          await PromptService.savePrompt(userId, data, text);
        } catch (e) {
          console.warn('Skip one prompt migration', e);
        }
      }
      localStorage.setItem(FLAG, '1');
    }
  } catch {
    // Silently ignore migration errors,not critical.
  }
}

const PromptBuilder = () => {
  const { user, signOut } = useAuth();
  const { enableChatbot, disableChatbot } = useChatbot();
  const [activeTab, setActiveTab] = useState('builder');
  const [promptData, setPromptData] = useState(initialPromptData);
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedFeatureCategory, setSelectedFeatureCategory] = useState('all');

  useEffect(() => {
    (async () => {
      const userId = getUserIdMaybe();
      try {
        const rows = await PromptService.getUserPrompts(userId);
        setSavedPrompts(rows);
      } catch (e) {
        console.error('NCB read failed; falling back to localStorage', e);
        const local = JSON.parse(localStorage.getItem('savedPrompts') || '[]');
        setSavedPrompts(local);
      }
      migrateLocalPromptsOnce();
    })();
  }, []);

  useEffect(() => {
    if (promptData.includeAiChatbot) {
      enableChatbot(promptData.chatbotConfig);
    } else {
      disableChatbot();
    }
    // Dispatch event for chatbot provider to listen
    window.dispatchEvent(new CustomEvent('promptDataChanged'));
  }, [promptData.includeAiChatbot, promptData.chatbotConfig, enableChatbot, disableChatbot]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPromptData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleNestedInputChange = (e, parentKey) => {
    const { name, value, type, checked } = e.target;
    setPromptData(prev => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [name]: type === 'checkbox' ? checked : value,
      },
    }));
  };

  const handleBusinessInfoChange = (e) => {
    const { name, value } = e.target;
    setPromptData(prev => ({
      ...prev,
      businessInfo: {
        ...prev.businessInfo,
        [name]: value,
      },
    }));
  };

  const handleAdminFeaturesChange = (feature, checked) => {
    setPromptData(prev => ({
      ...prev,
      adminFeatures: {
        ...prev.adminFeatures,
        [feature]: checked,
      },
    }));
  };

  const handleFeatureToggle = (featureId) => {
    setPromptData(prev => {
      const newFeatures = prev.features.includes(featureId)
        ? prev.features.filter(f => f !== featureId)
        : [...prev.features, featureId];
      return { ...prev, features: newFeatures };
    });
  };

  const generatePrompt = useCallback(() => {
    const prompt = PromptTemplate.generate(promptData);
    setGeneratedPrompt(prompt);
  }, [promptData]);

  useEffect(() => {
    generatePrompt();
  }, [generatePrompt]);

  const copyPrompt = () => {
    navigator.clipboard.writeText(generatedPrompt).then(() => {
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 2000);
    });
  };

  const savePrompt = async () => {
    try {
      const userId = getUserIdMaybe();
      const nameSuggestion = `Prompt ${savedPrompts.length + 1}`;
      const entered = window.prompt('Enter a name for this prompt:', nameSuggestion);
      if (entered === null) return; // cancel

      const namedData = { ...promptData };
      const row = await PromptService.savePrompt(userId, namedData, generatedPrompt);

      const uiItem = {
        id: row.id,
        name: row.name || entered,
        prompt: row.encrypted_prompt || generatedPrompt,
        createdAt: row.created_at || new Date().toISOString(),
        data: {
          category: row.category ?? promptData.category,
          projectType: row.project_type ?? promptData.projectType,
          complexity: row.complexity ?? promptData.complexity,
          framework: row.framework ?? promptData.framework,
          styling: row.styling ?? promptData.styling,
          features: row.features ?? promptData.features,
          customRequirements: row.custom_requirements ?? promptData.customRequirements,
          buildNotes: row.build_notes ?? promptData.buildNotes,
          context: row.context ?? promptData.context,
          constraints: row.constraints ?? promptData.constraints,
          siteTitle: row.site_title ?? promptData.siteTitle,
          logoUrl: row.logo_url ?? promptData.logoUrl,
          faviconUrl: row.favicon_url ?? promptData.faviconUrl,
          isPwa: !!(row.is_pwa ?? promptData.isPwa),
          hideFromSearchEngines: !!(row.hide_from_search_engines ?? promptData.hideFromSearchEngines),
          includeAdminConsole: !!(row.include_admin_console ?? promptData.includeAdminConsole),
          adminFeatures: row.admin_features ?? promptData.adminFeatures,
          adminCredentials: {
            useCustomCredentials: !!(row.has_custom_credentials ?? (promptData.adminCredentials?.useCustomCredentials))
          },
        },
      };

      setSavedPrompts(prev => [...prev, uiItem]);
    } catch (err) {
      console.error('Save failed:', err);
      // local fallback
      const fallback = {
        id: Date.now(),
        name: 'Local Prompt',
        prompt: generatedPrompt,
        data: promptData,
        createdAt: new Date().toISOString()
      };
      const updated = [...savedPrompts, fallback];
      setSavedPrompts(updated);
      localStorage.setItem('savedPrompts', JSON.stringify(updated));
    }
  };

  const loadPrompt = (promptToLoad) => {
    setPromptData(promptToLoad.data);
    setGeneratedPrompt(promptToLoad.prompt);
    setActiveTab('builder');
  };

  const deletePrompt = async (id) => {
    const userId = getUserIdMaybe();
    try {
      await PromptService.deletePrompt(userId, id);
      setSavedPrompts(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      console.error('Delete via NCB failed; falling back to local removal', e);
      const updated = savedPrompts.filter(p => p.id !== id);
      setSavedPrompts(updated);
      localStorage.setItem('savedPrompts', JSON.stringify(updated));
    }
  };

  const selectTemplate = (templateData) => {
    setPromptData(prev => ({ ...prev, ...templateData }));
    setActiveTab('builder');
  };

  const handleSignOut = async () => {
    await signOut();
  };

  const openInstructionsInNewTab = () => {
    const { hostServer, backendDatabase, storageOption, aiProvider } = promptData;
    const queryParams = new URLSearchParams({
      hosting: hostServer,
      backend: backendDatabase,
      storage: storageOption,
      aiProvider: aiProvider,
    }).toString();
    // Using window.open with the hash router format
    window.open(`${window.location.origin}${window.location.pathname}#/instructions?${queryParams}`, '_blank', 'noopener,noreferrer');
  };

  const navItems = [
    { id: 'builder', label: 'Builder', icon: FiEdit3 },
    { id: 'categories', label: 'Categories', icon: FiLayers },
    { id: 'saved', label: 'Saved', icon: FiSave },
    { id: 'settings', label: 'Settings', icon: FiSettings },
  ];

  const filteredFeatures = selectedFeatureCategory === 'all'
    ? featureOptions
    : featureOptions.filter(f => f.category === selectedFeatureCategory);


  const renderContent = () => {
    switch (activeTab) {
      case 'categories':
        return <PromptCategories onSelectTemplate={selectTemplate} />;
      case 'saved':
        return <SavedPrompts prompts={savedPrompts} onLoad={loadPrompt} onDelete={deletePrompt} />;
      case 'settings':
        return <Settings />;
      case 'builder':
      default:
        return (
          <div className="space-y-8">

            {/* Site Identity Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                <SafeIcon icon={FiGlobe} className="mr-2 text-blue-500" /> Site Identity
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Site Title
                  </label>
                  <input
                    type="text"
                    name="siteTitle"
                    value={promptData.siteTitle}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="My Awesome App"
                  />
                  <FieldDescription>
                    The main title of your application that will appear in headers and browser tabs
                  </FieldDescription>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Logo URL
                  </label>
                  <input
                    type="text"
                    name="logoUrl"
                    value={promptData.logoUrl}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com/logo.png"
                  />
                  <FieldDescription>
                    Direct link to your logo image (PNG,SVG,or JPG) for use in the application header
                  </FieldDescription>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Favicon URL
                  </label>
                  <input
                    type="text"
                    name="faviconUrl"
                    value={promptData.faviconUrl}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com/favicon.png"
                  />
                  <FieldDescription>
                    Small icon (16x16 or 32x32 pixels) that appears in browser tabs and bookmarks
                  </FieldDescription>
                </div>
              </div>
            </div>

            {/* Project Configuration Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Project Configuration</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Category
                  </label>
                  <select
                    name="category"
                    value={promptData.category}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {categoryOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <FieldDescription>
                    General domain or industry for your project to guide AI recommendations
                  </FieldDescription>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Project Type
                  </label>
                  <select
                    name="projectType"
                    value={promptData.projectType}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {(projectTypeOptions[promptData.category] || projectTypeOptions.other).map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <FieldDescription>
                    Specific type of application to generate optimized code structure
                  </FieldDescription>
                </div>
                {(promptData.category === 'other' || promptData.projectType === 'other') && (
                  <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {promptData.category === 'other' && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Custom Category
                        </label>
                        <input
                          type="text"
                          name="customCategory"
                          value={promptData.customCategory}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., IoT Application"
                        />
                        <FieldDescription>
                          Specify your custom project category for more accurate recommendations
                        </FieldDescription>
                      </div>
                    )}
                    {promptData.projectType === 'other' && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Custom Project Type
                        </label>
                        <input
                          type="text"
                          name="customProjectType"
                          value={promptData.customProjectType}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., Custom Dashboard"
                        />
                        <FieldDescription>
                          Describe your unique project type for tailored implementation
                        </FieldDescription>
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Complexity
                  </label>
                  <select
                    name="complexity"
                    value={promptData.complexity}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {complexityOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <FieldDescription>
                    Determines the depth of features and sophistication of the generated code
                  </FieldDescription>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Framework
                  </label>
                  <select
                    name="framework"
                    value={promptData.framework}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {frameworkOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <FieldDescription>
                    Primary development framework for implementation (can be overridden by AI recommendations)
                  </FieldDescription>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Styling
                  </label>
                  <select
                    name="styling"
                    value={promptData.styling}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {stylingOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <FieldDescription>
                    CSS methodology or library for styling the application interface
                  </FieldDescription>
                </div>
              </div>
            </div>

            {/* Features Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Features</h2>
              <div className="mb-4">
                <div className="flex flex-wrap gap-2 mb-4">
                  <button onClick={() => setSelectedFeatureCategory('all')} className={`px-3 py-1 rounded-full text-sm ${selectedFeatureCategory === 'all' ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}>
                    All Features
                  </button>
                  <button onClick={() => setSelectedFeatureCategory('core')} className={`px-3 py-1 rounded-full text-sm ${selectedFeatureCategory === 'core' ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}>
                    Core
                  </button>
                  <button onClick={() => setSelectedFeatureCategory('ui')} className={`px-3 py-1 rounded-full text-sm ${selectedFeatureCategory === 'ui' ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}>
                    UI/UX
                  </button>
                  <button onClick={() => setSelectedFeatureCategory('backend')} className={`px-3 py-1 rounded-full text-sm ${selectedFeatureCategory === 'backend' ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}>
                    Backend
                  </button>
                  <button onClick={() => setSelectedFeatureCategory('pwa')} className={`px-3 py-1 rounded-full text-sm ${selectedFeatureCategory === 'pwa' ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}>
                    PWA
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {filteredFeatures.map(feature => (
                    <div
                      key={feature.id}
                      onClick={() => handleFeatureToggle(feature.id)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${promptData.features.includes(feature.id) ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                    >
                      <div className="flex items-center">
                        <div className={`w-4 h-4 rounded border mr-2 flex items-center justify-center ${promptData.features.includes(feature.id) ? 'bg-blue-500 border-blue-500' : 'border-slate-300'}`}>
                          {promptData.features.includes(feature.id) && (
                            <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className="text-sm">{feature.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <FieldDescription>
                  Select functionality to include in your application. AI will optimize implementation based on these selections.
                </FieldDescription>
              </div>
            </div>

            {/* PWA & SEO Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                <SafeIcon icon={FiSmartphone} className="mr-2 text-purple-500" /> Progressive Web App & SEO
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="isPwa"
                      name="isPwa"
                      type="checkbox"
                      checked={promptData.isPwa}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="isPwa" className="font-medium text-slate-700">
                      Progressive Web App (PWA)
                    </label>
                    <FieldDescription>
                      Enable installable app with offline support,push notifications,and native-like experience
                    </FieldDescription>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="hideFromSearchEngines"
                      name="hideFromSearchEngines"
                      type="checkbox"
                      checked={promptData.hideFromSearchEngines}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="hideFromSearchEngines" className="font-medium text-slate-700">
                      Hide from Search Engines
                    </label>
                    <FieldDescription>
                      Prevent search engine indexing for private/internal applications with noindex tags
                    </FieldDescription>
                  </div>
                </div>
              </div>
            </div>

            {/* Design Reference Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                <SafeIcon icon={FiImage} className="mr-2 text-green-500" /> Design Reference
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Design Concepts URL
                  </label>
                  <input
                    type="text"
                    name="designConceptsUrl"
                    value={promptData.designConceptsUrl}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com/design-reference"
                  />
                  <FieldDescription>
                    URL to a website or design that should be used as a visual reference for styling and layout
                  </FieldDescription>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="includeSitemapAnalysis"
                        name="includeSitemapAnalysis"
                        type="checkbox"
                        checked={promptData.includeSitemapAnalysis}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="includeSitemapAnalysis" className="font-medium text-slate-700">
                        Include Sitemap Analysis
                      </label>
                      <FieldDescription>
                        Analyze the reference site's navigation structure and page hierarchy for similar organization
                      </FieldDescription>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="includeContentStructure"
                        name="includeContentStructure"
                        type="checkbox"
                        checked={promptData.includeContentStructure}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="includeContentStructure" className="font-medium text-slate-700">
                        Include Content Structure Analysis
                      </label>
                      <FieldDescription>
                        Analyze how content is organized and presented on the reference site for similar layouts
                      </FieldDescription>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Admin Console Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                <SafeIcon icon={FiShield} className="mr-2 text-red-500" /> Admin Console
              </h2>

              <div className="mb-6">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="includeAdminConsole"
                      name="includeAdminConsole"
                      type="checkbox"
                      checked={promptData.includeAdminConsole}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="includeAdminConsole" className="font-medium text-slate-700">
                      Include Admin Console
                    </label>
                    <FieldDescription>
                      Add a comprehensive admin panel for managing the application with user authentication
                    </FieldDescription>
                  </div>
                </div>
              </div>
              {promptData.includeAdminConsole && (
                <div className="space-y-6">
                  <div>
                    <h3 className="font-medium text-slate-800 mb-3">Admin Features</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {Object.entries(promptData.adminFeatures).map(([key, value]) => (
                        <div key={key} className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id={`adminFeature-${key}`}
                              name={key}
                              type="checkbox"
                              checked={value}
                              onChange={(e) => handleAdminFeaturesChange(key, e.target.checked)}
                              className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor={`adminFeature-${key}`} className="font-medium text-slate-700">
                              {key.split(/(?=[A-Z])/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                            </label>
                            <FieldDescription>
                              {key === 'contentManagement' && 'Create,edit,and organize all website content'}
                              {key === 'userManagement' && 'Manage user accounts,roles,and permissions'}
                              {key === 'analytics' && 'Track performance metrics and generate reports'}
                              {key === 'mediaStorage' && 'Upload and manage images,videos,and files'}
                              {key === 'customBranding' && 'Upload logo and customize colors and text'}
                              {key === 'seoManagement' && 'Manage meta tags,sitemaps,and SEO settings'}
                              {key === 'businessInfo' && 'Manage business address,contact details,and social links'}
                              {key === 'operatingHours' && 'Set business hours,special hours,and holidays'}
                              {key === 'onlineBooking' && 'Appointment system for service-based businesses'}
                              {key === 'menuManagement' && 'Manage menu items,categories,prices,and specials'}
                              {key === 'rolePermissions' && 'Granular access control with custom roles'}
                              {key === 'backupRestore' && 'System backup and restore functionality'}
                              {key === 'apiManagement' && 'Manage API keys and external integrations'}
                              {key === 'customFields' && 'Create custom data fields and forms'}
                              {!['contentManagement', 'userManagement', 'analytics', 'mediaStorage', 'customBranding', 'seoManagement', 'businessInfo', 'operatingHours', 'onlineBooking', 'menuManagement', 'rolePermissions', 'backupRestore', 'apiManagement', 'customFields'].includes(key) && 'Enable this administrative feature'}
                            </FieldDescription>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="border-t border-slate-200 pt-6">
                    <h3 className="font-medium text-slate-800 mb-3">Admin Credentials</h3>
                    <div className="flex items-start mb-4">
                      <div className="flex items-center h-5">
                        <input
                          id="useCustomCredentials"
                          name="useCustomCredentials"
                          type="checkbox"
                          checked={promptData.adminCredentials.useCustomCredentials}
                          onChange={(e) => handleNestedInputChange(e, 'adminCredentials')}
                          className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="useCustomCredentials" className="font-medium text-slate-700">
                          Use Custom Credentials
                        </label>
                        <FieldDescription>
                          Specify custom username and password for admin access instead of defaults
                        </FieldDescription>
                      </div>
                    </div>
                    {promptData.adminCredentials.useCustomCredentials && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Username
                          </label>
                          <input
                            type="text"
                            name="username"
                            value={promptData.adminCredentials.username}
                            onChange={(e) => handleNestedInputChange(e, 'adminCredentials')}
                            className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="admin"
                          />
                          <FieldDescription>
                            Administrator username for logging into the admin console
                          </FieldDescription>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Password
                          </label>
                          <input
                            type="password"
                            name="password"
                            value={promptData.adminCredentials.password}
                            onChange={(e) => handleNestedInputChange(e, 'adminCredentials')}
                            className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="••••••••"
                          />
                          <FieldDescription>
                            Secure password for administrator access (minimum 8 characters recommended)
                          </FieldDescription>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Business Information Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                <SafeIcon icon={FiUsers} className="mr-2 text-indigo-500" /> Business Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={promptData.businessInfo.companyName}
                    onChange={handleBusinessInfoChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your Company Name"
                  />
                  <FieldDescription>
                    Official business name for display in footers,contact pages,and legal documents
                  </FieldDescription>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Business Email
                  </label>
                  <input
                    type="email"
                    name="businessEmail"
                    value={promptData.businessInfo.businessEmail}
                    onChange={handleBusinessInfoChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="info@yourcompany.com"
                  />
                  <FieldDescription>
                    Primary business contact email for customer inquiries and communications
                  </FieldDescription>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={promptData.businessInfo.phoneNumber}
                    onChange={handleBusinessInfoChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+1 (555) 123-4567"
                  />
                  <FieldDescription>
                    Primary business contact phone number for customer calls
                  </FieldDescription>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Website URL
                  </label>
                  <input
                    type="url"
                    name="websiteUrl"
                    value={promptData.businessInfo.websiteUrl}
                    onChange={handleBusinessInfoChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://yourcompany.com"
                  />
                  <FieldDescription>
                    Main business website URL for linking and reference
                  </FieldDescription>
                </div>
              </div>
              <div className="mt-6">
                <h3 className="font-medium text-slate-800 mb-3">Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Address Line 1
                    </label>
                    <input
                      type="text"
                      name="addressLine1"
                      value={promptData.businessInfo.addressLine1}
                      onChange={handleBusinessInfoChange}
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="123 Main Street"
                    />
                    <FieldDescription>
                      Primary street address for the business location
                    </FieldDescription>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={promptData.businessInfo.city}
                      onChange={handleBusinessInfoChange}
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="New York"
                    />
                    <FieldDescription>City where the business is located</FieldDescription>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      State/Province
                    </label>
                    <input
                      type="text"
                      name="stateProvince"
                      value={promptData.businessInfo.stateProvince}
                      onChange={handleBusinessInfoChange}
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="NY"
                    />
                    <FieldDescription>State or province of the business address</FieldDescription>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      value={promptData.businessInfo.postalCode}
                      onChange={handleBusinessInfoChange}
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="10001"
                    />
                    <FieldDescription>ZIP or postal code for the business address</FieldDescription>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Country
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={promptData.businessInfo.country}
                      onChange={handleBusinessInfoChange}
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="USA"
                    />
                    <FieldDescription>Country where the business is located</FieldDescription>
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <h3 className="font-medium text-slate-800 mb-3">Social Media</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Facebook
                    </label>
                    <input
                      type="url"
                      name="socialFacebook"
                      value={promptData.businessInfo.socialFacebook}
                      onChange={handleBusinessInfoChange}
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://facebook.com/yourcompany"
                    />
                    <FieldDescription>Link to your business Facebook page</FieldDescription>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Twitter
                    </label>
                    <input
                      type="url"
                      name="socialTwitter"
                      value={promptData.businessInfo.socialTwitter}
                      onChange={handleBusinessInfoChange}
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://twitter.com/yourcompany"
                    />
                    <FieldDescription>Link to your business Twitter profile</FieldDescription>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Instagram
                    </label>
                    <input
                      type="url"
                      name="socialInstagram"
                      value={promptData.businessInfo.socialInstagram}
                      onChange={handleBusinessInfoChange}
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://instagram.com/yourcompany"
                    />
                    <FieldDescription>Link to your business Instagram profile</FieldDescription>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      LinkedIn
                    </label>
                    <input
                      type="url"
                      name="socialLinkedin"
                      value={promptData.businessInfo.socialLinkedin}
                      onChange={handleBusinessInfoChange}
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://linkedin.com/company/yourcompany"
                    />
                    <FieldDescription>Link to your business LinkedIn page</FieldDescription>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Pages Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Additional Pages</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="includePrivacyPage"
                      name="includePrivacyPage"
                      type="checkbox"
                      checked={promptData.includePrivacyPage}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="includePrivacyPage" className="font-medium text-slate-700">
                      Privacy Policy Page
                    </label>
                    <FieldDescription>
                      Add a comprehensive privacy policy page with customizable content
                    </FieldDescription>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="includeTermsPage"
                      name="includeTermsPage"
                      type="checkbox"
                      checked={promptData.includeTermsPage}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="includeTermsPage" className="font-medium text-slate-700">
                      Terms of Service Page
                    </label>
                    <FieldDescription>
                      Add terms and conditions page with legal terms and user agreements
                    </FieldDescription>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="includeContactPage"
                      name="includeContactPage"
                      type="checkbox"
                      checked={promptData.includeContactPage}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="includeContactPage" className="font-medium text-slate-700">
                      Contact Page
                    </label>
                    <FieldDescription>
                      Add contact information and form for customer inquiries
                    </FieldDescription>
                  </div>
                </div>
              </div>
              {promptData.includeContactPage && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="contactFormEnabled"
                        name="contactFormEnabled"
                        type="checkbox"
                        checked={promptData.contactFormEnabled}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="contactFormEnabled" className="font-medium text-slate-700">
                        Enable Contact Form
                      </label>
                      <FieldDescription>
                        Include a functional contact form on the contact page with validation
                      </FieldDescription>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Integrations Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                <SafeIcon icon={FiDatabase} className="mr-2 text-teal-500" /> Integrations
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Backend Database
                  </label>
                  <select
                    name="backendDatabase"
                    value={promptData.backendDatabase}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {backendDatabaseOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <FieldDescription>Select the backend database solution for your application</FieldDescription>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Host Server
                  </label>
                  <select
                    name="hostServer"
                    value={promptData.hostServer}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {hostServerOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <FieldDescription>Choose where your application will be hosted</FieldDescription>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Storage Option
                  </label>
                  <select
                    name="storageOption"
                    value={promptData.storageOption}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {storageOptionOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <FieldDescription>Select the file storage solution for your application</FieldDescription>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    AI Provider
                  </label>
                  <select
                    name="aiProvider"
                    value={promptData.aiProvider}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {aiProviderOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <FieldDescription>Select the AI provider for your application</FieldDescription>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    AI Model
                  </label>
                  <select
                    name="aiModel"
                    value={promptData.aiModel}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {aiModelOptions[promptData.aiProvider].map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <FieldDescription>Select the AI model for your application</FieldDescription>
                </div>
              </div>
            </div>


            {/* Additional Context Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Additional Context</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Project Context
                  </label>
                  <textarea
                    name="context"
                    value={promptData.context}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Provide any additional context about the project, target audience, or business goals..."
                  ></textarea>
                  <FieldDescription>
                    Any specific information about your project,target audience,business model,or special requirements
                  </FieldDescription>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Constraints
                  </label>
                  <textarea
                    name="constraints"
                    value={promptData.constraints}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="List any technical, business, or design constraints that should be considered..."
                  ></textarea>
                  <FieldDescription>
                    Technical limitations,budget constraints,timeline requirements,or other restrictions
                  </FieldDescription>
                </div>
              </div>
            </div>

            {/* Custom Requirements Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Custom Requirements</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Specific Requirements
                  </label>
                  <textarea
                    name="customRequirements"
                    value={promptData.customRequirements}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Add a pricing page with 3 tiers, implement user onboarding flow, include dark mode toggle..."
                  ></textarea>
                  <FieldDescription>
                    Any specific features, functionality, or implementation details not covered by other fields
                  </FieldDescription>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Build Notes (Optional)</label>
                  <textarea
                    name="buildNotes"
                    value={promptData.buildNotes}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Built by Greta for Project Phoenix. Repo: github.com/user/repo"
                  ></textarea>
                  <FieldDescription>
                    Internal notes for reference, such as author, repository, or project context. This will not affect the generated code.
                  </FieldDescription>
                </div>
              </div>
            </div>


            {/* Output Format Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Output Format</h2>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Desired Output Format
                </label>
                <select
                  name="outputFormat"
                  value={promptData.outputFormat}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {outputFormatOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <FieldDescription>
                  Specify what type of code structure you want the AI to generate
                </FieldDescription>
              </div>
            </div>

            {/* Preview Section */}
            <PromptPreview
              prompt={generatedPrompt}
              onCopy={copyPrompt}
              onSave={savePrompt}
              showCopySuccess={showCopySuccess}
              promptData={promptData}
            />

            {/* Integration Instructions Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-slate-800 flex items-center">
                  <SafeIcon icon={FiLink} className="mr-2 text-blue-500" /> Integration Setup Instructions
                </h2>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={openInstructionsInNewTab}
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                  <span>Open in New Tab</span>
                </motion.button>
              </div>
              <div className="mt-4">
                <Instructions
                  hosting={promptData.hostServer}
                  backend={promptData.backendDatabase}
                  storage={promptData.storageOption}
                  aiProvider={promptData.aiProvider}
                />
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 flex flex-col">
      <header className="bg-white/80 backdrop-blur-lg sticky top-0 z-30 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751704966743-favicon.png"
                alt="Promptly by RHT"
                className="w-8 h-8 rounded-lg"
                onError={(e) => { e.target.style.display = 'none'; setLogoError(true); }}
              />
              {logoError && <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center text-white font-bold">P</div>}
              <h1 className="text-xl font-bold text-slate-800">Promptly</h1>
            </div>
            <div className="hidden md:flex items-center space-x-2">
              {navItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === item.id ? 'bg-red-100 text-red-700' : 'text-slate-600 hover:bg-slate-100'}`}
                >
                  <SafeIcon icon={item.icon} className="inline-block w-4 h-4 mr-2" />
                  {item.label}
                </button>
              ))}
            </div>
            <div className="flex items-center space-x-4">
              {user ? (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-slate-600 hidden sm:inline">{user.email}</span>
                  <button onClick={handleSignOut} className="p-2 hover:bg-slate-100 rounded-full">
                    <SafeIcon icon={FiLogOut} className="w-5 h-5 text-slate-600" />
                  </button>
                </div>
              ) : (
                <button onClick={() => alert('Please go to Settings to sign in.')} className="text-sm font-medium text-red-600 hover:text-red-800">
                  Sign In
                </button>
              )}
              <div className="md:hidden">
                <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2">
                  <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
          {isMenuOpen && (
            <div className="md:hidden py-2">
              {navItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => { setActiveTab(item.id); setIsMenuOpen(false); }}
                  className={`w-full text-left flex items-center px-3 py-2 text-base font-medium rounded-md ${activeTab === item.id ? 'bg-red-100 text-red-700' : 'text-slate-600 hover:bg-slate-100'}`}
                >
                  <SafeIcon icon={item.icon} className="w-5 h-5 mr-3" />
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>
      <main className="flex-1">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
      <Footer promptData={promptData} onNavigate={setActiveTab} />
    </div>
  );
};

export default PromptBuilder;